import { postJsMessage } from '../../bridge';
import API from '../../api';
import { PostMessageOption } from '../../types';
import {
  NativeAPIResponseMessage,
  platformBridgeChecker,
} from '@zoom/hybrid-jsbridge';
import type { Response } from '../../utils/type';
import { base64ToArrayBuffer } from '../../utils/decode';
import { createError, ERROR_CODE } from '../../error';
import { compareSemVer, genError, isAvailable } from '../utils';
import { getSystemParam } from '../platform';
import type {
  UnifyWebviewPathIntention,
  OpenSysDlgSelectPathRequest,
  OpenSysDlgSelectPathResponse,
  OpenSysDlgSelectDirectoryRequest,
  OpenSysDlgSelectDirectoryResponse,
  SaveAsFileRequest,
  SaveAsFileResponse,
  FilePickerRequest,
  FileInfo,
  FileAbility,
  FileRangeInfo,
  FileRangeResponse,
  FileDataResponse,
  OpenSysFileExplorerRequest,
  CheckPathExistsRequest,
  CheckPathExistsResult,
  SavePathIdToDBRequest,
  SavePathIdToDBResult,
  OpenFileBySystemAppRequest,
} from './types';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.openSysDlgSelectPath
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function openSysDlgSelectPath(
  filename: string,
  intention: UnifyWebviewPathIntention,
  options?: PostMessageOption
): Response<OpenSysDlgSelectPathResponse> {
  if (!filename) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
        apiName: API.openSysDlgSelectPath,
        message: `filename can't be empty!`,
      })
    );
  }

  await isAvailable('filesystem.openSysDlgSelectPath');

  const systemParam = await getSystemParam();
  const params: OpenSysDlgSelectPathRequest = { filename };
  /**
   * Due to security requirements, this API requires the intention field to be passed starting from version 6.4.0.
   * the intention field will be directly discarded on clients prior to version 6.4.0.
   */
  if (compareSemVer(systemParam.clientVersion, '6.4.0') >= 0) {
    params.intention = intention;
  }

  return postJsMessage<
    OpenSysDlgSelectPathRequest,
    OpenSysDlgSelectPathResponse
  >(API.openSysDlgSelectPath, params, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.openSysDlgSelectDirectory
 *
 * [PLATFORM]
 * mac        6.4.5
 * windows    6.4.5
 * linux      6.5.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function openSysDlgSelectDirectory(
  intention: UnifyWebviewPathIntention,
  options?: PostMessageOption
): Response<OpenSysDlgSelectDirectoryResponse> {
  await isAvailable('filesystem.openSysDlgSelectDirectory');

  return postJsMessage<
    OpenSysDlgSelectDirectoryRequest,
    OpenSysDlgSelectDirectoryResponse
  >(API.openSysDlgSelectDirectory, { intention }, options);
}

function isArrayBufferLike(obj: any) {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    typeof obj.byteLength === 'number' &&
    typeof obj.slice === 'function'
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.saveAsFile
 *
 * [PLATFORM]
 * mac        6.2.10+
 * windows    6.2.10+
 * linux      6.2.10+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/

export async function saveAsFile(file: {
  fileStream: string | ArrayBufferLike;
  pathId?: string;
}): Response<SaveAsFileResponse> {
  await isAvailable('filesystem.saveAsFile');

  const { fileStream, pathId } = file;
  const base64 = normalizeToBase64(fileStream);

  if (!base64) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.FILE_SYSTEM_SAVE_FILE_FAILED,
        apiName: API.saveAsFile,
        message: `save file failed!`,
      })
    );
  }

  const maxSize = 20 * 1024 * 1024;
  if (base64.length >= maxSize) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.EXCEED_MAX_FILE_SIZE,
        apiName: API.saveAsFile,
        message: `save file failed, exceeds size limit(20MB)!`,
      })
    );
  }

  return postJsMessage<SaveAsFileRequest, SaveAsFileResponse>(API.saveAsFile, {
    fileStream: base64,
    pathId,
  });
}

export function normalizeToBase64(
  data: string | ArrayBufferLike
): string | null {
  if (typeof data === 'string') {
    return data;
  }
  if (isArrayBufferLike(data)) {
    return window.btoa(String.fromCharCode(...new Uint8Array(data)));
  }
  return null;
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.openFilePicker
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function openFilePicker(
  filePickerRequest: FilePickerRequest,
  options?: PostMessageOption
): Promise<NativeAPIResponseMessage<FileInfo[]>> {
  await isAvailable('filesystem.openFilePicker');
  if (options?.timeout === undefined) {
    if (options) {
      options.timeout = 0;
    } else {
      options = { timeout: 0 };
    }
  }
  return postJsMessage(API.openFilePicker, filePickerRequest, options);
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.getFileAbility
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getFileAbility(
  options?: PostMessageOption
): Promise<FileAbility | undefined> {
  await isAvailable('filesystem.getFileAbility');

  const response = await postJsMessage(API.getFileAbility, {}, options);
  const { returnCode, result, errorMsg } = response;
  if (returnCode === 0 && result) {
    return result as FileAbility;
  }

  throw createError({
    api: 'filesystem.getFileAbility',
    code: ERROR_CODE.FILE_FAILED_TO_GET_FILE_ABILITY,
    message: `[@zoom/hybrid-jssdk] Failed to getFileAbility: returnCode=${returnCode}, errorMsg=${errorMsg}`,
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.getFileInfo
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function getFileInfo(
  pathId: string,
  options?: PostMessageOption
): Promise<FileInfo | undefined> {
  await isAvailable('filesystem.getFileInfo');

  const response = await postJsMessage(API.getFileInfo, { pathId }, options);
  const { returnCode, result, errorMsg } = response;

  if (returnCode === 0 && result) {
    return result as FileInfo;
  }

  throw createError({
    api: 'filesystem.getFileInfo',
    code: ERROR_CODE.FILE_FAILED_TO_GET_FILE_INFO,
    message: `[@zoom/hybrid-jssdk] Failed to getFileInfo: returnCode=${returnCode}, errorMsg=${errorMsg}`,
  });
}

/**
 * Create virtual URL for fetch request
 */
function createVirtualUrl(path: string, fileRangeInfo: FileRangeInfo): string {
  const params = new URLSearchParams({
    pathId: fileRangeInfo.pathId,
    ...(fileRangeInfo.rangeStart !== undefined && {
      rangeStart: fileRangeInfo.rangeStart.toString(),
    }),
    ...(fileRangeInfo.rangeEnd !== undefined && {
      rangeEnd: fileRangeInfo.rangeEnd.toString(),
    }),
  });
  return `${window.location.origin}/${path}?${params.toString()}`;
}

/**
 * Calculate chunks for large range requests
 * Returns an array of range info objects for each chunk
 */
function calculateChunks(
  fileRangeInfo: FileRangeInfo,
  maxRangeSize: number
): FileRangeInfo[] {
  const chunks: FileRangeInfo[] = [];
  const start = fileRangeInfo.rangeStart ?? 0;
  const end = fileRangeInfo.rangeEnd ?? 0;
  const totalSize = end - start;

  for (let offset = 0; offset < totalSize; offset += maxRangeSize) {
    const chunkStart = start + offset;
    const chunkEnd = Math.min(chunkStart + maxRangeSize, end);
    chunks.push({
      pathId: fileRangeInfo.pathId,
      rangeStart: chunkStart,
      rangeEnd: chunkEnd,
    });
  }

  return chunks;
}

/**
 * Merge multiple ArrayBuffers into one
 */
function mergeArrayBuffers(buffers: ArrayBuffer[]): ArrayBuffer {
  const totalLength = buffers.reduce((sum, buf) => sum + buf.byteLength, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;

  for (const buffer of buffers) {
    result.set(new Uint8Array(buffer), offset);
    offset += buffer.byteLength;
  }

  return result.buffer;
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.read
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function read(
  fileRangeInfo: FileRangeInfo,
  options?: PostMessageOption
): Promise<FileDataResponse> {
  await isAvailable('filesystem.read');

  const ability = await getFileAbility(options);
  if (!ability) {
    throw createError({
      api: 'filesystem.read',
      code: ERROR_CODE.FILE_FAILED_TO_GET_FILE_ABILITY,
      message: '[@zoom/hybrid-jssdk] Failed to getFileAbility',
    });
  }

  // Prepare range information
  const { rangeStart, rangeEnd } = await prepareRangeInfo(fileRangeInfo);
  const finalRangeInfo = {
    pathId: fileRangeInfo.pathId,
    rangeStart,
    rangeEnd,
  };

  const fileData = await (ability.supportHttpFetchInterrupt
    ? readWithFetch(finalRangeInfo, ability)
    : readWithJsBridge(finalRangeInfo, ability, options));
  return {
    returnCode: 0,
    result: fileData,
  };
}

/**
 * Helper function to prepare range information
 */
async function prepareRangeInfo(
  fileRangeInfo: FileRangeInfo
): Promise<{ rangeStart: number; rangeEnd: number }> {
  const isReadFullFile =
    fileRangeInfo.rangeEnd === undefined &&
    fileRangeInfo.rangeStart === undefined;

  if (isReadFullFile) {
    const fileInfo = await getFileInfo(fileRangeInfo.pathId);
    if (!fileInfo) {
      throw createError({
        api: 'filesystem.read',
        code: ERROR_CODE.FILE_FAILED_TO_GET_FILE_INFO,
        message: '[@zoom/hybrid-jssdk] Failed to getFileInfo',
      });
    }
    return { rangeStart: 0, rangeEnd: fileInfo.fileSize };
  }

  // Validate range
  if (
    fileRangeInfo.rangeEnd !== undefined &&
    fileRangeInfo.rangeStart !== undefined &&
    fileRangeInfo.rangeEnd <= fileRangeInfo.rangeStart
  ) {
    throw createError({
      api: 'filesystem.read',
      code: ERROR_CODE.FILE_READ_INVALID_RANGE,
      message: `[@zoom/hybrid-jssdk] Invalid rangeStart:${fileRangeInfo.rangeStart} rangeEnd:${fileRangeInfo.rangeEnd}`,
    });
  }

  const rangeStart = fileRangeInfo.rangeStart ?? 0;
  const rangeEnd = fileRangeInfo.rangeEnd ?? 0;

  return {
    rangeStart,
    rangeEnd,
  };
}

/**
 * Read file data using fetch API with improved chunk handling
 */
async function readWithFetch(
  fileRangeInfo: Required<FileRangeInfo>,
  ability: FileAbility
): Promise<FileRangeResponse> {
  const totalSize = fileRangeInfo.rangeEnd - fileRangeInfo.rangeStart;

  // Single chunk case
  if (totalSize <= ability.maxRangeSize) {
    return fetchSingleChunk(fileRangeInfo, ability);
  }
  // Multiple chunks case
  const chunks = calculateChunks(fileRangeInfo, ability.maxRangeSize);
  const chunkResponses = await Promise.all(
    chunks.map((chunk) => fetchSingleChunk(chunk, ability))
  );

  // Find the overall range
  const minStart = Math.min(...chunkResponses.map((r) => r.rangeStart));
  const maxEnd = Math.max(...chunkResponses.map((r) => r.rangeEnd));

  return {
    fileData: mergeArrayBuffers(chunkResponses.map((r) => r.fileData)),
    rangeStart: minStart,
    rangeEnd: maxEnd,
  };
}

/**
 * Fetch a single chunk of file data
 */
async function fetchSingleChunk(
  chunk: FileRangeInfo,
  ability: FileAbility
): Promise<FileRangeResponse> {
  const response = await fetch(
    createVirtualUrl(ability.httpFetchInterruptPath, chunk),
    {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const rangeStart =
    parseInt(
      getHeaderCaseInsensitive(response.headers, 'range-start') || '0'
    ) ||
    chunk.rangeStart ||
    0;
  const rangeEnd =
    parseInt(getHeaderCaseInsensitive(response.headers, 'range-end') || '0') ||
    chunk.rangeEnd ||
    0;

  // android webview fetch not support rangeEnd, so we need to calculate the data by hand. @
  let binaryData: ArrayBuffer = new ArrayBuffer(0);
  if (ability.supportBinary) {
    if (platformBridgeChecker.isAndroid(window)) {
      if (response.body) {
        const reader = response.body.getReader();
        const length = rangeEnd - rangeStart;
        let receivedLength = 0;
        const chunks = [];

        while (receivedLength < length) {
          const { done, value } = await reader.read();
          if (done) break;

          if (receivedLength + value.length > length) {
            const excess = receivedLength + value.length - length;
            chunks.push(value.slice(0, value.length - excess));
            receivedLength = length;
          } else {
            chunks.push(value);
            receivedLength += value.length;
          }
        }

        const buffer = new Uint8Array(length);
        let position = 0;
        for (const chunk of chunks) {
          buffer.set(chunk, position);
          position += chunk.length;
        }
        binaryData = buffer.buffer;
      }
    } else {
      binaryData = await response.arrayBuffer();
    }
  }

  const fileData = ability.supportBinary
    ? binaryData
    : base64ToArrayBuffer(await response.text());

  return { fileData, rangeStart, rangeEnd };
}

/**
 * Read file data using JSBridge with improved error handling
 */
async function readWithJsBridge(
  fileRangeInfo: Required<FileRangeInfo>,
  ability: FileAbility,
  options?: PostMessageOption
): Promise<FileRangeResponse> {
  const totalSize = fileRangeInfo.rangeEnd - fileRangeInfo.rangeStart;

  // Single chunk case
  if (totalSize <= ability.maxRangeSize) {
    return bridgeReadSingleChunk(fileRangeInfo, ability, options);
  }

  // Multiple chunks case
  const chunks = calculateChunks(fileRangeInfo, ability.maxRangeSize);
  const chunkResponses = await Promise.all(
    chunks.map((chunk) => bridgeReadSingleChunk(chunk, ability, options))
  );

  // Find the overall range
  const minStart = Math.min(...chunkResponses.map((r) => r.rangeStart));
  const maxEnd = Math.max(...chunkResponses.map((r) => r.rangeEnd));

  return {
    fileData: mergeArrayBuffers(chunkResponses.map((r) => r.fileData)),
    rangeStart: minStart,
    rangeEnd: maxEnd,
  };
}

/**
 * Read a single chunk using JSBridge
 */
async function bridgeReadSingleChunk(
  chunk: FileRangeInfo,
  ability: FileAbility,
  options?: PostMessageOption
): Promise<FileRangeResponse> {
  const response = await postJsMessage<FileRangeInfo, FileRangeResponse>(
    API.read,
    chunk,
    options
  );

  if (response.returnCode !== 0 || !response.result) {
    throw new Error(
      response.errorMsg ?? '[@zoom/hybrid-jssdk] Failed to read file chunks'
    );
  }

  if (ability.supportBinary) {
    return response.result;
  }

  return {
    fileData: base64ToArrayBuffer(
      response.result.fileData as unknown as string
    ),
    rangeStart: response.result.rangeStart,
    rangeEnd: response.result.rangeEnd,
  };
}

function getHeaderCaseInsensitive(
  headers: Headers,
  name: string
): string | null {
  const headerKey = Array.from(headers.keys()).find(
    (k) => k.toLowerCase() === name.toLowerCase()
  );

  return headerKey ? headers.get(headerKey) : null;
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.openSysFileExplorer
 *
 * [PLATFORM]
 * mac        6.5.0
 * windows    6.5.0
 * linux      6.5.0
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function openSysFileExplorer(
  fileExplorer: OpenSysFileExplorerRequest,
  options?: PostMessageOption
): Response<unknown> {
  await isAvailable('filesystem.openSysFileExplorer');
  return postJsMessage<OpenSysFileExplorerRequest, unknown>(
    API.openSysFileExplorer,
    fileExplorer,
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.checkPathExists
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        6.6.0
 * android    6.7.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function checkPathExists(
  checkPathRequest: CheckPathExistsRequest,
  options?: PostMessageOption
): Response<CheckPathExistsResult> {
  await isAvailable('filesystem.checkPathExists');
  return postJsMessage<CheckPathExistsRequest, CheckPathExistsResult>(
    API.checkPathExists,
    checkPathRequest,
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.savePathIdToDB
 *
 * [PLATFORM]
 * mac        6.6.0
 * windows    6.6.0
 * linux      6.6.0
 * ios        6.6.0
 * android    6.6.0
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function savePathIdToDB(
  savePathIdRequest: SavePathIdToDBRequest,
  options?: PostMessageOption
): Response<SavePathIdToDBResult> {
  await isAvailable('filesystem.savePathIdToDB');
  return postJsMessage<SavePathIdToDBRequest, SavePathIdToDBResult>(
    API.savePathIdToDB,
    savePathIdRequest,
    options
  );
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * filesystem.openFileBySystemApp
 *
 * [PLATFORM]
 * mac        6.6.0+
 * windows    6.6.0+
 * linux      6.6.0+
 * ios        NotSupport
 * android    NotSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function openFileBySystemApp(
  pathId: string,
  extensions: string[],
  intention: UnifyWebviewPathIntention,
  options?: PostMessageOption
): Response<unknown> {
  if (!pathId || !extensions || extensions.length === 0) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
        apiName: API.openFileBySystemApp,
        message: `pathId and extensions can't be empty! ${pathId} ${JSON.stringify(extensions)}`,
      })
    );
  }

  await isAvailable('filesystem.openFileBySystemApp');

  return postJsMessage<
    OpenFileBySystemAppRequest,
    NativeAPIResponseMessage<unknown>
  >(API.openFileBySystemApp, { pathId, extensions, intention }, options);
}

export default {
  openSysDlgSelectPath,
  openSysDlgSelectDirectory,
  openSysFileExplorer,
  saveAsFile,
  openFilePicker,
  getFileAbility,
  getFileInfo,
  checkPathExists,
  savePathIdToDB,
  read,
  openFileBySystemApp,
};
